import type { ReactNode, Ref } from 'react';
import { Modal, type ModalSize } from '@/components/ui/Modal';

interface LabConfiguratorShellProps {
  title: string;
  stepLabel?: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  stepIndex?: number;
  totalSteps?: number;
  onClose?: () => void;
  closeDisabled?: boolean;
  maxWidthClassName?: string;
  size?: ModalSize;
  bodyClassName?: string;
  scrollRef?: Ref<HTMLDivElement>;
  bodyKey?: string;
}

export function LabConfiguratorShell({
  title,
  stepLabel,
  description,
  icon,
  children,
  footer,
  headerActions,
  stepIndex = 0,
  totalSteps = 1,
  onClose,
  closeDisabled,
  maxWidthClassName = 'max-w-3xl',
  size,
  bodyClassName = '',
  scrollRef,
  bodyKey,
}: LabConfiguratorShellProps) {
  const modalSize = size || (maxWidthClassName.includes('5xl') || maxWidthClassName.includes('6xl') ? 'wide' : '2xl');
  const close = onClose || (() => undefined);
  return (
    <Modal
      isOpen
      onClose={close}
      title={(
        <div className="flex flex-wrap items-center gap-3">
          {icon && <div className="flex shrink-0 text-accent-primary">{icon}</div>}
          <span>{title}</span>
          {stepLabel && <span className="rounded-lg border border-accent-primary/30 bg-accent-primary/15 px-3 py-1 text-sm font-semibold text-accent-primary">{stepLabel}</span>}
        </div>
      )}
      description={description}
      footer={footer}
      headerActions={headerActions}
      size={modalSize}
      closeOnBackdrop={Boolean(onClose) && !closeDisabled}
      closeOnEscape={Boolean(onClose) && !closeDisabled}
      hideCloseButton={!onClose || closeDisabled}
      bodyRef={scrollRef}
      bodyKey={bodyKey}
      bodyClassName={`space-y-6 [mask-image:linear-gradient(to_bottom,transparent,black_12px,black_calc(100%-18px),transparent)] ${bodyClassName}`}
    >
        {totalSteps > 1 && (
          <div className="flex items-center justify-center gap-2 pb-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex ? 'w-8 bg-accent-primary' :
                  i < stepIndex ? 'w-4 bg-accent-primary/40' : 'w-4 bg-border-strong'
                }`}
              />
            ))}
          </div>
        )}

        {children}
    </Modal>
  );
}
